import { v4 as uuidv4 } from "uuid";
import { doc, collection, getDocs, query, where, orderBy, setDoc, onSnapshot, deleteDoc } from "firebase/firestore";

import { db } from "../../../../firebase/firebase";

import { Message } from "../../types";
import { MessageContent } from "../../../GINO/types/openAI";

export const getUserChats = async (userId: number) => {
  let q = query(collection(db, "Threads"));
  q = query(q, where("participants", "array-contains", userId));
  q = query(q, orderBy("lastMessage", "desc"));

  return (await getDocs(q)).docs.map((doc) => ({ ...doc.data(), id: doc.id }));
};

export const getUserChatsIds = async (userId: number) => {
  let q = query(collection(db, "Threads"));
  q = query(q, where("participants", "array-contains", userId));
  q = query(q, orderBy("lastMessage", "desc"));

  return (await getDocs(q)).docs.map((doc) => doc.id);
};

export const sendMessage = async (threadId: string, message: string, senderId: number) => {
  const UUID = uuidv4();
  const c = collection(doc(db, "Threads", threadId), "Messages");
  const q = query(c);
  const data = {
    createdAt: Date.now(),
    data: { text: message },
    senderId: senderId,
    uuid: UUID,
    type: "TEXT",
  };
  await setDoc(doc(c), data);
  const threadRef = doc(db, "Threads", threadId);
  await setDoc(threadRef, { lastMessage: data }, { merge: true });
};

export const sendGINOMessage = async (threadId: string, message: MessageContent, senderId: number) => {
  const UUID = uuidv4();
  const c = collection(doc(db, "Threads", threadId), "Messages");
  const q = query(c);
  const data = {
    createdAt: Date.now(),
    data: message,
    senderId: senderId,
    uuid: UUID,
    type: "GINO",
  };
  await setDoc(doc(c), data);
  const threadRef = doc(db, "Threads", threadId);
  await setDoc(threadRef, { lastMessage: data }, { merge: true });
};

export const getThreadId = async (senderId: number, receiverId: number) => {
  const q = query(
    collection(db, "Threads"),
    where("participants", "==", [Math.min(senderId, receiverId), Math.max(senderId, receiverId)])
  );
  const thread = await getDocs(q);

  if (thread.empty) {
    let q = doc(collection(db, "Threads"));
    await setDoc(q, {
      counts: { [senderId]: 0, [receiverId]: 0 },
      createdAt: Date.now(),
      lastSeen: { [senderId]: 0, [receiverId]: 0 },
      participants: [Math.min(senderId, receiverId), Math.max(senderId, receiverId)],
    });
    return q.id;
  }
  return thread.docs[0].id;
};

export const createChat = async (
  message: string,
  senderId: number,
  receiverId: number,
  senderName: string,
  receiverName: string
) => {
  // First, try to find an existing thread between the two users
  const threadId = await getThreadIdBetweenTheTwoUsers(senderId, receiverId);

  if (!threadId) {
    // If no thread exists, create a new one
    const threadId = await createThreadIdBetweenTheTwoUsers(senderId, receiverId, senderName, receiverName);

    await sendMessage(threadId, message, senderId);
    return threadId;
  } else {
    // If thread already exists, use the existing thread ID
    await sendMessage(threadId, message, senderId);
    return threadId;
  }
};

export const createThreadIdBetweenTheTwoUsers = async (
  senderId: number,
  receiverId: number,
  senderName: string,
  receiverName: string
) => {
  let q = doc(collection(db, "Threads"));
  await setDoc(q, {
    counts: { [senderId]: 0, [receiverId]: 0 },
    createdAt: Date.now(),
    lastSeen: { [senderId]: 0, [receiverId]: 0 },
    participants: [Math.min(senderId, receiverId), Math.max(senderId, receiverId)],
    participantsNames: { [senderId]: senderName, [receiverId]: receiverName },
  });

  return q.id;
};

export const getThreadIdBetweenTheTwoUsers = async (senderId: number, receiverId: number) => {
  const q = query(
    collection(db, "Threads"),
    where("participants", "==", [Math.min(senderId, receiverId), Math.max(senderId, receiverId)])
  );
  const thread = await getDocs(q);

  if (thread.empty) {
    // No existing thread found
    return null;
  }
  return thread.docs[0].id; // Return the existing thread ID
};

export const subscribeForChatUpdate = async (userId: number, callback: (chatList: any) => void) => {
  let q = query(collection(db, "Threads"));
  q = query(q, where("participants", "array-contains", userId));
  q = query(q, orderBy("lastMessage", "desc"));

  // Set up the listener
  const unsubscribe = onSnapshot(
    q,
    (snapshot) => {
      try {
        const chatList = snapshot.docs.map((doc) => ({
          ...doc.data(),
          id: doc.id,
        }));
        callback(chatList);
      } catch (error) {
        console.error("Error handling Firestore snapshot:", error);
      }
    },
    (error) => {
      console.error("Firestore snapshot error:", error);
    }
  );

  return unsubscribe;
};

export const updateLastSeenChat = async (chatId: string, userId: number) => {
  let q = doc(collection(db, "Threads"), chatId);
  await setDoc(
    q,
    {
      lastSeen: { [userId]: Date.now() },
    },
    { merge: true }
  );
};

export const subscribeToMessages = (threadId: string, callback: (messages: Message[]) => void) => {
  const messagesCollection = collection(doc(db, "Threads", threadId), "Messages");
  const q = query(messagesCollection, orderBy("createdAt", "asc"));

  const unsubscribe = onSnapshot(q, (snapshot) => {
    const messages = snapshot.docs.map((doc) => doc.data()) as Message[];
    callback(messages);
  });

  return unsubscribe;
};

export const clearThreadMessages = async (threadId: string) => {
  const messagesCollection = collection(doc(db, "Threads", threadId), "Messages");
  const q = query(messagesCollection);
  const snapshot = await getDocs(q);
  snapshot.forEach(async (doc) => await deleteDoc(doc.ref));
};
